import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import moment from "moment";
import { jwtDecode } from "jwt-decode";
import Cookies from "js-cookie";
import axios from "axios";
import Sidebar from "./Sidebar";
import { PurchaseOrderPreview } from "./purchaseOrderPreview";

const PurchaseOrders = () => {
  const navigate = useNavigate();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSidebarItem, setActiveSidebarItem] = useState("purchase-order");
  const [user, setUser] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [formValues, setFormValues] = useState(null);

  const handleSidebarItemClick = (itemId, path) => {
    navigate(path);
    setActiveSidebarItem(itemId);
  };
  const handleView = async(order) => {
    setViewMode(true);
    setFormValues(order);
  }

  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUser(decodedToken);
      } catch (err) {
        console.error(err);
      }
    }
  }, []);

  useEffect(() => {
    const fetchPurchaseOrders = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/get-purchase-orders`,
          {
            withCredentials: true,
          },
        );
        setPurchaseOrders(response.data);
      } catch (error) {
        console.error("Error fetching purchase orders:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPurchaseOrders();
  }, []);

  const getStatusColor = (status) => {
    const colors = {
      PENDING: "bg-yellow-100 text-yellow-800 ring-yellow-600/20",
      COMPLETED: "bg-green-100 text-green-800 ring-green-600/20",
      CANCELLED: "bg-red-100 text-red-800 ring-red-600/20",
    };
    return colors[status] || "bg-gray-100 text-gray-800 ring-gray-600/20";
  };

  const handleApprove = async (order, e) => {
    e.stopPropagation();
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/update-purchase-order`,
        { status: "APPROVED", orderId: order._id },
        { withCredentials: true },
      );
      window.location.reload();
    } catch (error) {
      console.error("Error approving order:", error);
    }
  };

  const handleReject = async (order, e) => {
    e.stopPropagation();
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/update-purchase-order/${order._id}`,
        { status: "REJECTED" },
        { withCredentials: true },
      );
      window.location.reload();
    } catch (error) {
      console.error("Error rejecting order:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar
          activeSidebarItem={activeSidebarItem}
          onSidebarItemClick={handleSidebarItemClick}
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex  bg-gray-50 h-max">

      <Sidebar
        activeSidebarItem={activeSidebarItem}
        onSidebarItemClick={handleSidebarItemClick}
      />
      {viewMode ? (<PurchaseOrderPreview formValues= {formValues} viewMode={viewMode} />):
      (
      <div className="flex-1 overflow-auto">
        <div className="ml-64 p-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Purchase Orders
                </h1>
                <p className="mt-2 text-sm text-gray-600">
                  Manage and track all your purchase orders
                </p>
              </div>
              <button
                onClick={() => navigate("/create-purchase-order")}
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create Purchase Order
              </button>
            </div>

            <div className="bg-white shadow-sm rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Seller
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Buyer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Due Date
                      </th>
                   
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Amount
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {purchaseOrders.map((order) => (
                      <tr className="hover:bg-gray-50 transition-colors cursor-pointer">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {order?.seller}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {order.buyer}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {moment(order.orderDate).format("MMM DD, YYYY")}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {moment(order.dueDate).format("MMM DD, YYYY")}
                        </td>
                       
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                          ₹{order.totalAmount.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button onClick={() => handleView(order)}>View</button>

                        </td>
                      </tr>
                    ))}
                    {purchaseOrders.length === 0 && (
                      <tr>
                        <td colSpan="7" className="px-6 py-16 text-center">
                          <div className="text-center">
                            <h3 className="text-lg font-medium text-gray-900">
                              No purchase orders found
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">
                              Get started by creating a new purchase order
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default PurchaseOrders;
